import React, { useCallback, useEffect, useState } from 'react';
import { money, statusBadge, statusCls } from '../utils/order';

import ReportService from '../services/report.service';
import Spinner from './Spinner';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const TABS = ['orders', 'customers', 'products'];
const SOURCES = ['all', 'shop', 'table'];

const isoDate = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

// Compare two cell values: numbers (incl. numeric strings like DECIMALs)
// numerically, everything else as locale-aware text. Empty values sort last.
const compareValues = (a, b, locale) => {
  const empty = (v) => v === null || v === undefined || v === '';
  if (empty(a) || empty(b)) return empty(a) && empty(b) ? 0 : empty(a) ? 1 : -1;
  const na = Number(a);
  const nb = Number(b);
  if (!isNaN(na) && !isNaN(nb)) return na - nb;
  return String(a).localeCompare(String(b), locale, { numeric: true, sensitivity: 'base' });
};

const StatCard = ({ label, value }) => (
  <div className='col-6 col-lg-3 mb-3'>
    <div className='card h-100'>
      <div className='card-body'>
        <div className='small text-muted'>{label}</div>
        <div className='fs-5 fw-semibold'>{value}</div>
      </div>
    </div>
  </div>
);

const Reports = () => {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState('orders');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return isoDate(d);
  });
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [productSort, setProductSort] = useState('quantity');
  const [source, setSource] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Client-side column sorting, one state per table: { [table]: { key, dir } }.
  // Clicking a header cycles asc -> desc -> original (server) order.
  const [sorts, setSorts] = useState({});
  const toggleSort = (table, key) =>
    setSorts((all) => {
      const cur = all[table];
      if (!cur || cur.key !== key) return { ...all, [table]: { key, dir: 'asc' } };
      if (cur.dir === 'asc') return { ...all, [table]: { key, dir: 'desc' } };
      const { [table]: _removed, ...rest } = all;
      return rest;
    });
  // getters: { [columnKey]: (row) => value }
  const sortRows = (table, rows, getters) => {
    const cur = sorts[table];
    if (!cur || !getters[cur.key] || !Array.isArray(rows)) return rows || [];
    const get = getters[cur.key];
    const sign = cur.dir === 'asc' ? 1 : -1;
    // Stable: ties keep the server order.
    return rows
      .map((row, i) => ({ row, i }))
      .sort((x, y) => sign * compareValues(get(x.row), get(y.row), i18n.language) || x.i - y.i)
      .map((x) => x.row);
  };
  const SortTh = ({ table, col, label, className = '' }) => {
    const cur = sorts[table];
    const active = cur && cur.key === col;
    return (
      <th
        className={`${className} user-select-none`}
        style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
        onClick={() => toggleSort(table, col)}
        aria-sort={active ? (cur.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        title={t('reports.sortByField', { field: label })}
      >
        {label} <span className={active ? '' : 'text-muted opacity-50'}>{active ? (cur.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </th>
    );
  };

  const load = useCallback(() => {
    setLoading(true);
    setData(null);
    const params = { from: from || undefined, to: to || undefined, limit: 20, source };
    if (tab === 'products') params.sort = productSort;
    ReportService[tab](params)
      .then((res) => setData(res.data))
      .catch((err) => toast.error((err.response && err.response.data && err.response.data.message) || err.message))
      .finally(() => setLoading(false));
  }, [tab, from, to, productSort, source]);

  useEffect(() => {
    load();
  }, [load]);

  const productName = (p) => {
    const lang = (i18n.language || 'vi').slice(0, 2);
    return (lang === 'en' ? p.name_en || p.name_vi : p.name_vi || p.name_en) || p.name;
  };

  const sourceLabel = (src) => t(`reports.source_${src}`);
  const statusCell = (src, status) => {
    if (src === 'table') {
      return <span className={`badge ${statusCls(status)}`}>{t(`tableOrderStatus.${status}`, status)}</span>;
    }
    return <span className={`badge ${statusCls(status)}`}>{t(`orderStatus.${status}`, statusBadge(status).label)}</span>;
  };
  const showSplit = source === 'all';

  const setPreset = (days) => {
    if (!days) {
      setFrom('');
      setTo('');
      return;
    }
    const d = new Date();
    d.setDate(d.getDate() - (days - 1));
    setFrom(isoDate(d));
    setTo(isoDate(new Date()));
  };

  // Which preset (days; 0 = all time) matches the current range, or null if
  // the dates were picked manually. Used to highlight the active button.
  const presetStart = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - (days - 1));
    return isoDate(d);
  };
  const activePreset = !from && !to ? 0 : to === isoDate(new Date()) ? [1, 7, 30].find((n) => presetStart(n) === from) ?? null : null;
  const presetCls = (days) => `btn ${activePreset === days ? 'btn-secondary active' : 'btn-outline-secondary'}`;

  const renderOrders = () => (
    <>
      <div className='row'>
        <StatCard label={t('reports.totalOrders')} value={data.summary.total_orders || 0} />
        <StatCard label={t('reports.validOrders')} value={data.summary.valid_orders || 0} />
        <StatCard label={t('reports.revenue')} value={money(data.summary.revenue)} />
        <StatCard label={t('reports.avgOrderValue')} value={money(data.summary.avg_order_value)} />
      </div>
      {showSplit && (
        <div className='mb-4'>
          <h5>{t('reports.bySource')}</h5>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                <SortTh table='bySource' col='source' label={t('reports.source')} />
                <SortTh table='bySource' col='count' label={t('reports.totalOrders')} className='text-end' />
                <SortTh table='bySource' col='valid' label={t('reports.validOrders')} className='text-end' />
                <SortTh table='bySource' col='revenue' label={t('reports.revenue')} className='text-end' />
              </tr>
            </thead>
            <tbody>
              {data.bySource.length === 0 && (
                <tr>
                  <td colSpan='4' className='text-muted'>
                    {t('reports.noData')}
                  </td>
                </tr>
              )}
              {sortRows('bySource', data.bySource, { source: (s) => sourceLabel(s.source), count: (s) => s.count, valid: (s) => s.valid_orders || 0, revenue: (s) => s.revenue || 0 }).map((s) => (
                <tr key={s.source}>
                  <td>{sourceLabel(s.source)}</td>
                  <td className='text-end'>{s.count}</td>
                  <td className='text-end'>{s.valid_orders || 0}</td>
                  <td className='text-end'>{money(s.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className='row'>
        <div className='col-12 mb-4'>
          <h5>{t('reports.byStatus')}</h5>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                {showSplit && <SortTh table='byStatus' col='source' label={t('reports.source')} />}
                <SortTh table='byStatus' col='status' label={t('reports.status')} />
                <SortTh table='byStatus' col='count' label={t('reports.count')} className='text-end' />
                <SortTh table='byStatus' col='amount' label={t('reports.amount')} className='text-end' />
              </tr>
            </thead>
            <tbody>
              {data.byStatus.length === 0 && (
                <tr>
                  <td colSpan={showSplit ? 4 : 3} className='text-muted'>
                    {t('reports.noData')}
                  </td>
                </tr>
              )}
              {sortRows('byStatus', data.byStatus, {
                source: (s) => sourceLabel(s.source),
                status: (s) => (s.source === 'table' ? t(`tableOrderStatus.${s.status}`, s.status) : t(`orderStatus.${s.status}`, s.status)),
                count: (s) => s.count,
                amount: (s) => s.amount || 0,
              }).map((s) => (
                <tr key={`${s.source}-${s.status}`}>
                  {showSplit && <td>{sourceLabel(s.source)}</td>}
                  <td>{statusCell(s.source, s.status)}</td>
                  <td className='text-end'>{s.count}</td>
                  <td className='text-end'>{money(s.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='col-12 mb-4'>
          <h5>{t('reports.byDay')}</h5>
          <div className='table-responsive rounded' style={{ maxHeight: 420 }}>
            <table className='table table-hover align-middle'>
              <thead>
                <tr>
                  <SortTh table='byDay' col='day' label={t('reports.date')} />
                  <SortTh table='byDay' col='count' label={t('reports.count')} className='text-end' />
                  {showSplit && <SortTh table='byDay' col='shop' label={t('reports.source_shop')} className='text-end' />}
                  {showSplit && <SortTh table='byDay' col='table' label={t('reports.source_table')} className='text-end' />}
                  <SortTh table='byDay' col='revenue' label={t('reports.revenue')} className='text-end' />
                </tr>
              </thead>
              <tbody>
                {data.byDay.length === 0 && (
                  <tr>
                    <td colSpan={showSplit ? 5 : 3} className='text-muted'>
                      {t('reports.noData')}
                    </td>
                  </tr>
                )}
                {sortRows('byDay', data.byDay, { day: (d) => d.day, count: (d) => d.count, shop: (d) => d.shop_count || 0, table: (d) => d.table_count || 0, revenue: (d) => d.revenue || 0 }).map((d) => (
                  <tr key={d.day}>
                    <td>{d.day}</td>
                    <td className='text-end'>{d.count}</td>
                    {showSplit && <td className='text-end'>{d.shop_count || 0}</td>}
                    {showSplit && <td className='text-end'>{d.table_count || 0}</td>}
                    <td className='text-end'>{money(d.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {source !== 'shop' && (
        <div className='mb-4'>
          <h5>{t('reports.byTable')}</h5>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                <SortTh table='byTable' col='table' label={t('reports.table')} />
                <SortTh table='byTable' col='count' label={t('reports.count')} className='text-end' />
                <SortTh table='byTable' col='revenue' label={t('reports.revenue')} className='text-end' />
              </tr>
            </thead>
            <tbody>
              {data.byTable.length === 0 && (
                <tr>
                  <td colSpan='3' className='text-muted'>
                    {t('reports.noData')}
                  </td>
                </tr>
              )}
              {sortRows('byTable', data.byTable, { table: (b) => b.table_name, count: (b) => b.count, revenue: (b) => b.revenue || 0 }).map((b) => (
                <tr key={b.table_name}>
                  <td>{b.table_name}</td>
                  <td className='text-end'>{b.count}</td>
                  <td className='text-end'>{money(b.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );

  const renderCustomers = () => (
    <>
      <div className='row'>
        <StatCard label={t('reports.activeCustomers')} value={data.summary.active_customers || 0} />
        <StatCard label={t('reports.newUsers')} value={data.summary.new_users || 0} />
        <StatCard label={t('reports.totalUsers')} value={data.summary.total_users || 0} />
        {source !== 'shop' && <StatCard label={t('reports.guestOrders')} value={`${data.summary.guest_orders || 0} · ${money(data.summary.guest_revenue)}`} />}
      </div>
      <h5>{t('reports.topCustomers')}</h5>
      <div className='table-responsive rounded'>
        <table className='table table-hover align-middle'>
          <thead>
            <tr>
              <SortTh table='customers' col='rank' label='#' />
              <SortTh table='customers' col='customer' label={t('reports.customer')} />
              <SortTh table='customers' col='email' label={t('reports.email')} />
              <SortTh table='customers' col='orders' label={t('reports.orders')} className='text-end' />
              {showSplit && <SortTh table='customers' col='shop' label={t('reports.source_shop')} className='text-end' />}
              {showSplit && <SortTh table='customers' col='table' label={t('reports.source_table')} className='text-end' />}
              <SortTh table='customers' col='spent' label={t('reports.spent')} className='text-end' />
              <SortTh table='customers' col='last' label={t('reports.lastOrder')} />
            </tr>
          </thead>
          <tbody>
            {data.top.length === 0 && (
              <tr>
                <td colSpan={showSplit ? 8 : 6} className='text-muted'>
                  {t('reports.noData')}
                </td>
              </tr>
            )}
            {sortRows('customers', data.top.map((c, i) => ({ ...c, _rank: i + 1 })), {
              rank: (c) => c._rank,
              customer: (c) => c.username || `#${c.user_id}`,
              email: (c) => c.email,
              orders: (c) => c.orders,
              shop: (c) => c.shop_orders || 0,
              table: (c) => c.table_orders || 0,
              spent: (c) => c.spent || 0,
              last: (c) => (c.last_order_at ? new Date(c.last_order_at).getTime() : null),
            }).map((c) => (
              <tr key={c.user_id}>
                <td>{c._rank}</td>
                <td>{c.username || `#${c.user_id}`}</td>
                <td>{c.email}</td>
                <td className='text-end'>{c.orders}</td>
                {showSplit && <td className='text-end'>{c.shop_orders || 0}</td>}
                {showSplit && <td className='text-end'>{c.table_orders || 0}</td>}
                <td className='text-end'>{money(c.spent)}</td>
                <td>{c.last_order_at ? new Date(c.last_order_at).toLocaleString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  const renderProducts = () => (
    <>
      <div className='row'>
        <StatCard label={t('reports.unitsSold')} value={data.summary.units_sold || 0} />
        <StatCard label={t('reports.productsSold')} value={data.summary.products_sold || 0} />
        <StatCard label={t('reports.revenue')} value={money(data.summary.revenue)} />
        <StatCard label={t('reports.totalProducts')} value={data.summary.total_products || 0} />
      </div>
      <div className='d-flex align-items-center mb-2 gap-2'>
        <h5 className='mb-0 me-auto'>{t('reports.topProducts')}</h5>
        <label className='small text-muted'>{t('reports.sortBy')}</label>
        <select className='form-select form-select-sm w-auto' value={productSort} onChange={(e) => setProductSort(e.target.value)}>
          <option value='quantity'>{t('reports.quantity')}</option>
          <option value='revenue'>{t('reports.revenue')}</option>
        </select>
      </div>
      <div className='table-responsive rounded mb-4'>
        <table className='table table-hover align-middle'>
          <thead>
            <tr>
              <SortTh table='topProducts' col='rank' label='#' />
              <SortTh table='topProducts' col='product' label={t('reports.product')} />
              <SortTh table='topProducts' col='quantity' label={t('reports.quantity')} className='text-end' />
              {showSplit && <SortTh table='topProducts' col='shop' label={t('reports.source_shop')} className='text-end' />}
              {showSplit && <SortTh table='topProducts' col='table' label={t('reports.source_table')} className='text-end' />}
              <SortTh table='topProducts' col='orders' label={t('reports.orders')} className='text-end' />
              <SortTh table='topProducts' col='revenue' label={t('reports.revenue')} className='text-end' />
            </tr>
          </thead>
          <tbody>
            {data.top.length === 0 && (
              <tr>
                <td colSpan={showSplit ? 7 : 5} className='text-muted'>
                  {t('reports.noData')}
                </td>
              </tr>
            )}
            {sortRows('topProducts', data.top.map((p, i) => ({ ...p, _rank: i + 1 })), {
              rank: (p) => p._rank,
              product: (p) => p.product_name,
              quantity: (p) => p.quantity,
              shop: (p) => p.shop_quantity || 0,
              table: (p) => p.table_quantity || 0,
              orders: (p) => p.orders,
              revenue: (p) => p.revenue || 0,
            }).map((p) => (
              <tr key={p.product_id || `x${p._rank}-${p.product_name}`}>
                <td>{p._rank}</td>
                <td>{p.product_name}</td>
                <td className='text-end'>{p.quantity}</td>
                {showSplit && <td className='text-end'>{p.shop_quantity || 0}</td>}
                {showSplit && <td className='text-end'>{p.table_quantity || 0}</td>}
                <td className='text-end'>{p.orders}</td>
                <td className='text-end'>{money(p.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h5>{t('reports.unsoldProducts')}</h5>
      <div className='table-responsive rounded'>
        <table className='table table-hover align-middle'>
          <thead>
            <tr>
              <SortTh table='unsold' col='id' label='ID' />
              <SortTh table='unsold' col='product' label={t('reports.product')} />
              <SortTh table='unsold' col='price' label={t('reports.price')} className='text-end' />
            </tr>
          </thead>
          <tbody>
            {data.unsold.length === 0 && (
              <tr>
                <td colSpan='3' className='text-muted'>
                  {t('reports.noData')}
                </td>
              </tr>
            )}
            {sortRows('unsold', data.unsold, { id: (p) => p.id, product: (p) => productName(p), price: (p) => p.price }).map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{productName(p)}</td>
                <td className='text-end'>{money(p.price)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  return (
    <div className='container mt-4'>
      <h3 className='mb-3'>{t('reports.title')}</h3>

      <ul className='nav nav-tabs mb-3'>
        {TABS.map((k) => (
          <li className='nav-item' key={k}>
            <button type='button' className={`nav-link ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
              {t(`reports.tab_${k}`)}
            </button>
          </li>
        ))}
      </ul>

      <div className='row g-2 align-items-end mb-3'>
        <div className='col-auto'>
          <label className='form-label small mb-1'>{t('reports.from')}</label>
          <input type='date' className='form-control form-control-sm' value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className='col-auto'>
          <label className='form-label small mb-1'>{t('reports.to')}</label>
          <input type='date' className='form-control form-control-sm' value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className='col-auto'>
          <label className='form-label small mb-1'>{t('reports.source')}</label>
          <select className='form-select form-select-sm' value={source} onChange={(e) => setSource(e.target.value)}>
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {sourceLabel(s)}
              </option>
            ))}
          </select>
        </div>
        <div className='col-auto'>
          <div className='btn-group btn-group-sm'>
            <button type='button' className={presetCls(1)} aria-pressed={activePreset === 1} onClick={() => setPreset(1)}>
              {t('reports.today')}
            </button>
            <button type='button' className={presetCls(7)} aria-pressed={activePreset === 7} onClick={() => setPreset(7)}>
              {t('reports.last7')}
            </button>
            <button type='button' className={presetCls(30)} aria-pressed={activePreset === 30} onClick={() => setPreset(30)}>
              {t('reports.last30')}
            </button>
            <button type='button' className={presetCls(0)} aria-pressed={activePreset === 0} onClick={() => setPreset(0)}>
              {t('reports.allTime')}
            </button>
          </div>
        </div>
        <div className='col-auto'>
          <button type='button' className='btn btn-sm btn-outline-primary' onClick={load} disabled={loading}>
            {t('reports.refresh')}
          </button>
        </div>
      </div>
      <p className='small text-muted'>{t('reports.note')}</p>

      {loading && <Spinner />}
      {!loading && data && tab === 'orders' && data.byStatus && data.byTable && renderOrders()}
      {!loading && data && tab === 'customers' && data.top && data.summary.total_users !== undefined && renderCustomers()}
      {!loading && data && tab === 'products' && data.unsold && renderProducts()}
    </div>
  );
};

export default Reports;
